// src/ui/WidgetShell.jsx
import React from "react";
import clsx from "clsx";

/**
 * Kokonut-style widget container used across the Mudo dashboard.
 *
 * Props:
 * - title?: string
 * - subtitle?: string
 * - icon?: ReactNode
 * - actions?: ReactNode              // right side of header (buttons, menus, etc.)
 * - toolbar?: ReactNode              // full-width row below header (filters, chips, etc.)
 * - tabs?: { id: string; label: string }[]
 * - activeTabId?: string
 * - onTabChange?: (id: string) => void
 * - draggable?: boolean              // shows a subtle drag handle on the left of header
 * - className?: string               // extra classes for outer shell
 * - bodyClassName?: string           // extra classes for inner body
 * - children: ReactNode
 */

export default function WidgetShell({
  title,
  subtitle,
  icon,
  actions,
  toolbar,
  tabs,
  activeTabId,
  onTabChange,
  draggable = false,
  className,
  bodyClassName,
  children,
}) {
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;

  return (
    <section
      className={clsx(
        "relative flex flex-col min-h-0",
        // Stronger, more readable glass for both light + dark
        "rounded-[22px] border border-[var(--mudo-border)]",
        "bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_8%)]",
        "backdrop-blur-xl shadow-[0_18px_45px_rgba(0,0,0,0.45)]",
        "transition-all duration-150 hover:bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_2%)]",
        className
      )}
    >
      {/* Header */}
      {(title || actions || draggable || icon) && (
        <header
          className={clsx(
            "flex items-center justify-between gap-3",
            "px-4 py-3",
            hasTabs || toolbar
              ? "border-b border-[var(--mudo-border)]/75"
              : ""
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            {draggable && (
              <span
                className="flex flex-col justify-center gap-0.5 pr-1 text-[10px] text-[var(--mudo-text-muted)] cursor-grab select-none"
                aria-hidden="true"
              >
                <span className="block h-0.5 w-3 rounded-full bg-[var(--mudo-border)]/80" />
                <span className="block h-0.5 w-3 rounded-full bg-[var(--mudo-border)]/80" />
              </span>
            )}

            {icon && (
              <span className="flex items-center justify-center h-7 w-7 rounded-lg bg-[var(--mudo-bg)]/85 border border-[var(--mudo-border)]/80 text-[var(--mudo-text)]">
                {icon}
              </span>
            )}

            <div className="min-w-0 leading-tight">
              {title && (
                <h2
                  className="truncate"
                  style={{
                    fontSize: "var(--mudo-text-size-header, 14px)",
                    fontWeight: "var(--mudo-font-weight-header, 600)",
                    letterSpacing: "-0.01em",
                    color: "var(--mudo-text)",
                  }}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className="truncate"
                  style={{
                    fontSize: "var(--mudo-text-size-small, 11px)",
                    fontWeight: "var(--mudo-font-weight-small, 400)",
                    color: "var(--mudo-text-muted)",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {actions && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </header>
      )}

      {/* Toolbar row directly under header (filters, pills, etc.) */}
      {toolbar && (
        <div className="px-4 py-2 border-b border-[var(--mudo-border)]/70 bg-[color-mix(in_lab,var(--mudo-bg),transparent_20%)]">
          <div className="flex flex-wrap items-center gap-2">{toolbar}</div>
        </div>
      )}

      {/* Optional tabs row */}
      {hasTabs && (
        <div className="px-3 pt-1 pb-2 border-b border-[var(--mudo-border)]/70 bg-[color-mix(in_lab,var(--mudo-bg),transparent_25%)]">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange && onTabChange(tab.id)}
                  className={clsx(
                    "px-3 py-1.5 rounded-full text-xs sm:text-[13px] whitespace-nowrap transition",
                    "border",
                    isActive
                      ? "border-[var(--mudo-primary)] bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,white)] shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
                      : "border-[var(--mudo-border)] bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_15%)] text-[var(--mudo-text-muted)] hover:bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_5%)]"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Body */}
      <div
        className={clsx(
          "flex-1 min-h-0 px-4 pb-4 pt-3",
          bodyClassName
        )}
        style={{
          fontSize: "var(--mudo-text-size-body, 13px)",
          fontWeight: "var(--mudo-font-weight-body, 500)",
          color: "var(--mudo-text)",
        }}
      >
        {children}
      </div>
    </section>
  );
}
